import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "@/hooks/use-toast";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { format } from "date-fns";
import { Calendar as CalendarIcon, Loader2, CalendarDays, List, Grid } from "lucide-react";
import { db } from "../firebase/config";
import { collection, getDocs } from "firebase/firestore";
import { getEventColor } from "@/lib/eventHelpers";

interface Term {
  id: string;
  number: number;
  title: string;
  openingDate?: Date;
  closingDate?: Date;
}

interface CalendarEvent {
  id: string;
  title: string;
  date: string; // yyyy-MM-dd
  type: string;
  description: string;
}

const parseYYYYMMDD = (value: string): Date | null => {
  if (!value) return null;
  const match = /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (!match) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const [y, m, d] = value.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const isWithinInclusive = (date: Date, start?: Date, end?: Date) => {
  if (!start || !end) return true;
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();
  return day >= s && day <= e;
};

const formatShort = (d?: Date) => {
  if (!d) return "-";
  return format(d, "MMM do, yyyy");
};

interface SchoolCalendarProps {
  schoolID: string | null;
}

const SchoolCalendar = ({ schoolID }: SchoolCalendarProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [terms, setTerms] = useState<Term[]>([]);
  const [selectedTermId, setSelectedTermId] = useState<string>("");
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(new Date());

  // Fetch terms and events
  useEffect(() => {
    const load = async () => {
      if (!schoolID) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      try {
        const [termsSnap, eventsSnap] = await Promise.all([
          getDocs(collection(db, `schools/${schoolID}/terms`)),
          getDocs(collection(db, `schools/${schoolID}/events`)),
        ]);

        const rawTerms: Term[] = termsSnap.docs
          .map((d) => {
            const data: any = d.data();
            return {
              id: d.id,
              number: data.number,
              title: data.title,
              openingDate: data.openingDate ? parseYYYYMMDD(data.openingDate) || undefined : undefined,
              closingDate: data.closingDate ? parseYYYYMMDD(data.closingDate) || undefined : undefined,
            };
          })
          .filter((t) => !!t.number)
          .sort((a, b) => a.number - b.number);

        // Deduplicate terms: prefer terms with dates, and prefer mostly recent/future dates
        const bestByNumber = new Map<number, Term>();
        for (const t of rawTerms) {
          const existing = bestByNumber.get(t.number);
          
          if (!existing) {
            bestByNumber.set(t.number, t);
            continue;
          }

          // 1. Prefer terms with valid dates
          const tHasDates = t.openingDate && t.closingDate;
          const eHasDates = existing.openingDate && existing.closingDate;

          if (tHasDates && !eHasDates) {
            bestByNumber.set(t.number, t);
            continue;
          }
          if (!tHasDates && eHasDates) {
            continue;
          }

          // 2. If both have dates (or neither), prefer the one with the LATER opening date (e.g. 2026 vs 2025)
          if (t.openingDate && existing.openingDate) {
             if (t.openingDate.getTime() > existing.openingDate.getTime()) {
                bestByNumber.set(t.number, t);
                continue;
             }
             if (t.openingDate.getTime() < existing.openingDate.getTime()) {
                continue;
             }
          }
          
          // 3. Tie-breaker: Title length or ID preferences (legacy)
           const scoreTerm = (term: Term) => {
              const titleLen = (term.title || "").trim().length;
              const numericId = /^\d+$/.test(term.id) ? 1 : 0;
              return titleLen * 2 + numericId;
           };
           
           if (scoreTerm(t) > scoreTerm(existing)) {
              bestByNumber.set(t.number, t);
           }
        }
        const termList: Term[] = Array.from(bestByNumber.values()).sort((a, b) => a.number - b.number);

        const eventList: CalendarEvent[] = eventsSnap.docs
          .map((d) => ({ id: d.id, ...(d.data() as any) }))
          .filter((e) => !!e.title && !!e.date);

        setTerms(termList);
        setEvents(eventList);

        // Default term logic
        const today = new Date();
        // 1. Try to find term where today is within range
        let found = termList.find((t) => isWithinInclusive(today, t.openingDate, t.closingDate));
        
        // 2. If not found (e.g. holiday), logic to find "relevant" term
        if (!found) {
            // Sort by openingDate to find chronological order
            const timeSorted = [...termList].sort((a, b) => {
                const aTime = a.openingDate?.getTime() || 0;
                const bTime = b.openingDate?.getTime() || 0;
                return aTime - bTime;
            });
            
            // Find first term that starts in the future (Upcoming term)
            found = timeSorted.find(t => t.openingDate && t.openingDate > today);
            
            // If no future term found, we might be at end of year -> pick the last term
            if (!found && timeSorted.length > 0) {
                 found = timeSorted[timeSorted.length - 1];
            }
        }

        const defaultId = (found || termList[0])?.id || "";
        setSelectedTermId((prev) => prev || defaultId);

        if (!selectedDate) setSelectedDate(today);
      } catch (error) {
        console.error("Error loading calendar:", error);
        toast({ title: "Error", description: "Failed to load school calendar.", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [schoolID]);

  const selectedTerm = useMemo(() => terms.find((t) => t.id === selectedTermId), [terms, selectedTermId]);

  const eventsInSelectedTerm = useMemo(() => {
    const start = selectedTerm?.openingDate;
    const end = selectedTerm?.closingDate;
    return events
      .map((e) => ({ ...e, _date: parseYYYYMMDD(e.date) }))
      .filter((e) => e._date && isWithinInclusive(e._date, start, end))
      .sort((a, b) => (a._date!.getTime() - b._date!.getTime()));
  }, [events, selectedTerm]);

  // Filter dayEvents from ALL events, not just those in the term
  const dayEvents = useMemo(() => {
    if (!selectedDate) return [] as (CalendarEvent & { _date?: Date | null })[];
    const dayKey = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate()).toDateString();
    
    // Use all events, not just eventsInSelectedTerm
    return events
      .map((e) => ({ ...e, _date: parseYYYYMMDD(e.date) }))
      .filter((e: any) => {
        const d: Date | null = e._date;
        if (!d) return false;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toDateString() === dayKey;
      });
  }, [events, selectedDate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!schoolID) {
    return (
      <Card className="border-dashed">
        <CardHeader>
          <CardTitle>School Calendar</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Unable to load your school calendar (missing school access).
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Term Info Card - Styled like AcademicsManagement */}
      <Card className="overflow-hidden border-none shadow-md bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-950">
        <CardHeader className="border-b border-indigo-100 dark:border-slate-800 bg-white/60 dark:bg-slate-900/60 backdrop-blur-sm">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
                <CalendarIcon className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                <CardTitle>School Calendar</CardTitle>
                <CardDescription>View academic terms, holidays, and school events</CardDescription>
              </div>
            </div>
            <div className="w-[200px]">
              <Select value={selectedTermId} onValueChange={setSelectedTermId}>
                <SelectTrigger className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 focus:ring-indigo-500">
                  <SelectValue placeholder="Select term" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
                  {terms.map((t) => (
                    <SelectItem key={t.id} value={t.id} className="focus:bg-indigo-50 dark:focus:bg-indigo-900/20 cursor-pointer">
                      Term {t.number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="space-y-1">
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Current Term</span>
              <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {selectedTerm?.title || "No Term Selected"}
              </p>
            </div>
            <div className="space-y-1">
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Opening Date</span>
              <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {selectedTerm?.openingDate ? format(selectedTerm.openingDate, "PPP") : "-"}
              </p>
            </div>
            <div className="space-y-1">
              <span className="text-sm font-medium text-slate-500 dark:text-slate-400">Closing Date</span>
              <p className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                {selectedTerm?.closingDate ? format(selectedTerm.closingDate, "PPP") : "-"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="calendar" className="w-full">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
            Academic Schedule
          </h2>
          <TabsList className="grid w-[200px] grid-cols-2">
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <Grid className="w-4 h-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="list" className="flex items-center gap-2">
              <List className="w-4 h-4" />
              List View
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="calendar" className="mt-0">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Left Column: Calendar Widget */}
            <Card className="lg:col-span-4 border-none shadow-md bg-white dark:bg-slate-950 h-fit overflow-hidden">
              <CardHeader className="pb-4 bg-slate-50/50 dark:bg-slate-900/50 border-b border-slate-100 dark:border-slate-800">
                <CardTitle className="text-lg flex items-center gap-2">
                  <CalendarDays className="w-5 h-5 text-indigo-500" />
                  Calendar View
                </CardTitle>
              </CardHeader>
              <CardContent className="flex justify-center p-4">
                <style>{`
                  .calendar-today-day button {
                    background-color: #22c55e !important;
                    color: white !important;
                    font-weight: bold !important;
                    border-radius: 0.375rem !important;
                  }
                  ${events.map(event => {
                    if (!event.date) return '';
                    const color = getEventColor(event.id || event.title);
                    // Parse date to get day number for class name
                    let dateObj: Date;
                    if (typeof event.date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(event.date)) {
                      const [year, month, day] = event.date.split('-').map(Number);
                      dateObj = new Date(year, month - 1, day);
                    } else {
                      dateObj = new Date(event.date);
                    }
                    const dateStr = dateObj.toLocaleDateString();
                    return `
                      td:has(button[data-day="${dateStr}"]) button {
                        background-color: ${color} !important;
                        color: white !important;
                        font-weight: bold !important;
                        border-radius: 0.375rem !important;
                      }
                    `;
                  }).join('')}
                `}</style>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm w-full max-w-[300px] mx-auto text-slate-900 dark:text-slate-100"
                  modifiersClassNames={{
                    today: "calendar-today-day"
                  }}
                />
              </CardContent>
              <div className="px-6 pb-6">
                <div className="text-sm text-muted-foreground">
                  <div className="flex flex-wrap gap-3 justify-center mt-2">
                    <p className="text-xs text-center w-full">
                      Select a date to view event details.
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            {/* Right Column: Events List */}
            <Card className="lg:col-span-8 border-none shadow-md bg-white dark:bg-slate-950 overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4 bg-slate-50/50 dark:bg-slate-900/50">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    {selectedDate ? (
                      <>
                        <span className="text-primary font-bold">
                          {selectedDate.getDate()}
                        </span>
                        {format(selectedDate, "MMMM yyyy")}
                      </>
                    ) : "Upcoming Events"}
                  </CardTitle>
                  <CardDescription>
                    {selectedDate ? format(selectedDate, "EEEE") : "Events in this term"}
                  </CardDescription>
                </div>
                {dayEvents.length > 0 && (
                  <Badge className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    {dayEvents.length} Event{dayEvents.length !== 1 ? 's' : ''}
                  </Badge>
                )}
              </CardHeader>
              
              <ScrollArea className="h-[600px] p-6">
                <div className="space-y-6">
                  {/* Selected Day Events */}
                  {selectedDate && (
                    <div className="space-y-4">
                      <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                        Scheduled for {format(selectedDate, "MMM do")}
                      </h3>
                      
                      {dayEvents.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-8 text-center border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-xl bg-slate-50/50 dark:bg-slate-900/50">
                          <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-full mb-3">
                            <CalendarDays className="h-6 w-6 text-slate-400" />
                          </div>
                          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">No events scheduled</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Select another date or check upcoming events below
                          </p>
                        </div>
                      ) : (
                        dayEvents.map((ev) => {
                          const eventColor = getEventColor(ev.id || ev.title);
                          return (
                            <div
                              key={ev.id}
                              className="group relative p-5 border border-slate-200 dark:border-slate-800 rounded-xl hover:shadow-md transition-all duration-200 hover:border-slate-300 dark:hover:border-slate-700"
                              style={{ 
                                backgroundColor: `${eventColor}08`,
                              }}
                            >
                              <div 
                                className="absolute top-0 left-0 w-1.5 h-full rounded-l-xl transition-opacity"
                                style={{ backgroundColor: eventColor }}
                              ></div>
                              <div className="flex items-start justify-between gap-4">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Badge 
                                      variant="outline" 
                                      className="border-opacity-50"
                                      style={{ 
                                        backgroundColor: `${eventColor}15`,
                                        color: eventColor,
                                        borderColor: `${eventColor}40`
                                      }}
                                    >
                                      {(ev.type || "Event").toUpperCase()}
                                    </Badge>
                                  </div>
                                  <h4 className="font-semibold text-lg text-slate-900 dark:text-slate-100">
                                    {ev.title}
                                  </h4>
                                  <p className="text-sm text-slate-600 dark:text-slate-300 line-clamp-2">
                                    {ev.description}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  )}

                  <Separator />

                  {/* Upcoming Events */}
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                      Upcoming in {selectedTerm?.title || "Term"}
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {eventsInSelectedTerm
                        .filter((e: any) => {
                           if (!e._date) return false;
                           // Use strict date comparison to ignore time
                           const eventDate = new Date(e._date.getFullYear(), e._date.getMonth(), e._date.getDate());
                           const today = new Date();
                           today.setHours(0,0,0,0);
                           return eventDate >= today;
                        })
                        .slice(0, 6)
                        .map((ev: any) => {
                          const eventColor = getEventColor(ev.id || ev.title);
                          return (
                            <div 
                              key={ev.id} 
                              className="group relative p-4 border border-slate-200 dark:border-slate-800 rounded-xl hover:shadow-md transition-all duration-200 hover:border-slate-300 dark:hover:border-slate-700"
                              style={{ 
                                backgroundColor: `${eventColor}05`,
                              }}
                            >
                              <div 
                                className="absolute top-0 left-0 w-1 h-full rounded-l-xl transition-opacity"
                                style={{ backgroundColor: eventColor }}
                              ></div>
                              <div className="flex items-start gap-3">
                                <div className="mt-1 w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: eventColor }} />
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="text-sm font-medium truncate text-slate-900 dark:text-slate-100">{ev.title}</span>
                                    <span className="text-xs text-slate-500 whitespace-nowrap">
                                      {formatShort(ev._date)}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{ev.description}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      
                      {eventsInSelectedTerm.length === 0 && (
                        <div className="col-span-full text-sm text-slate-500 dark:text-slate-400 italic text-center py-4">
                          No upcoming events found for this term.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </ScrollArea>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="list" className="mt-0">
          <Card className="border-none shadow-md bg-white dark:bg-slate-950">
            <CardHeader className="border-b border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>All Events</CardTitle>
                  <CardDescription>Complete chronological list of all academic events in {selectedTerm?.title.split(' ').pop() || new Date().getFullYear()}</CardDescription>
                </div>
                <Badge className="bg-indigo-600 hover:bg-indigo-700 text-white">
                  {eventsInSelectedTerm.length} Term Events
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[calc(100vh-300px)]">
                <div className="p-6 space-y-6">
                  {events.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-full mb-4">
                        <CalendarDays className="h-8 w-8 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">No events found</h3>
                      <p className="text-slate-500 dark:text-slate-400 mt-1">
                        There are no events scheduled in the calendar yet.
                      </p>
                    </div>
                  ) : (
                    events
                      .filter(ev => {
                         // Filter to show only events from the current year onwards, or from the selected term year
                         const evDate = parseYYYYMMDD(ev.date);
                         if (!evDate) return false;
                         const termYear = selectedTerm?.openingDate?.getFullYear() || new Date().getFullYear();
                         return evDate.getFullYear() >= termYear; 
                      })
                      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
                      .map((ev) => {
                        const eventColor = getEventColor(ev.id || ev.title);
                        const date = parseYYYYMMDD(ev.date);
                        
                        return (
                          <div 
                            key={ev.id} 
                            className="group flex flex-col sm:flex-row gap-4 p-4 border border-slate-200 dark:border-slate-800 rounded-xl hover:shadow-md transition-all duration-200 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-900 m-2"
                          >
                            <div className="flex sm:flex-col items-center sm:items-start gap-2 sm:gap-0 min-w-[120px] sm:border-r sm:border-slate-100 sm:dark:border-slate-800 sm:pr-4">
                              <span className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                                {date ? format(date, "dd") : "--"}
                              </span>
                              <span className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase">
                                {date ? format(date, "MMM yyyy") : "---"}
                              </span>
                              <span className="text-xs text-slate-400 hidden sm:block mt-1">
                                {date ? format(date, "EEEE") : ""}
                              </span>
                            </div>
                            
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge 
                                  variant="outline" 
                                  className="border-opacity-50"
                                  style={{ 
                                    backgroundColor: `${eventColor}15`,
                                    color: eventColor,
                                    borderColor: `${eventColor}40`
                                  }}
                                >
                                  {(ev.type || "Event").toUpperCase()}
                                </Badge>
                                {date && isWithinInclusive(new Date(), date, date) && (
                                  <Badge className="bg-green-500 hover:bg-green-600">Today</Badge>
                                )}
                              </div>
                              
                              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-1 group-hover:text-primary transition-colors">
                                {ev.title}
                              </h3>
                              
                              <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed">
                                {ev.description || "No description provided."}
                              </p>
                            </div>
                            
                            <div className="w-1.5 self-stretch rounded-full opacity-50 group-hover:opacity-100 transition-opacity" style={{ backgroundColor: eventColor }}></div>
                          </div>
                        );
                      })
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolCalendar;
